import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Stethoscope, Star, Languages, IndianRupee, Search, BadgeCheck, Send, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { isUserLoggedIn } from '../lib/auth';

const SPECIALTIES = ['', 'General Physician', 'Pediatrician', 'Cardiologist', 'Dermatologist', 'Gynecologist', 'ENT Specialist'];

export default function Doctors() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const reportId = params.get('report');
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [picked, setPicked] = useState(null);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [reportPreview, setReportPreview] = useState(null);
const load = useCallback(async () => {
  setLoading(true);
  try {
    const q = new URLSearchParams();
    if (city) q.set('city', city);
    if (specialty) q.set('specialty', specialty);

    const { data } = await api.get(`/doctors?${q.toString()}`);
    setDoctors(data.doctors);
  } catch (e) {
    toast.error('Could not load doctors');
  } finally {
    setLoading(false);
  }
}, [city, specialty]);
//   useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
  if (!isUserLoggedIn()) return nav('/login');
  load();
}, [load, nav])

  useEffect(() => {
    if (!reportId || !isUserLoggedIn()) return;
    api.get(`/report/${reportId}`)
      .then(({ data }) => setReportPreview(data))
      .catch(() => {});
  }, [reportId]);

  const sendReferral = async () => {
    if (!reportId) { toast.error('No report selected'); return; }
    if (!picked) return;
    setSending(true);
    try {
      await api.post('/referrals', { report_id: reportId, doctor_id: picked.id, patient_message: note });
      toast.success(`Report sent to ${picked.name}`);
      setPicked(null); setNote('');
      nav('/consultations');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send referral');
    } finally { setSending(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 fade-up">
      <div className="flex items-center justify-between mb-6">
        <Link to={reportId ? `/report/${reportId}` : '/'} className="flex items-center gap-2 text-[#1E3F33] font-semibold" data-testid="doctors-back-link">
          <ArrowLeft className="h-4 w-4"/> Back
        </Link>
      </div>
      <div className="max-w-2xl">
        <h1 className="font-display text-4xl sm:text-5xl text-[#1A1A1A]" data-testid="doctors-title">Find a doctor near you</h1>
        <p className="text-[#4A4A4A] text-lg mt-3">Pick a verified doctor and send your Health Buddy intake summary. They will review it and respond with clinical guidance.</p>
      </div>
      <Card className="mt-8 p-5 rounded-3xl border border-[#E8E1D5] bg-white hb-shadow-soft">
        <div className="grid sm:grid-cols-[1.5fr_1.2fr_auto] gap-3">
          <div className="relative">
            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#7A7A7A]"/>
            <Input value={city} onChange={(e)=>setCity(e.target.value)} placeholder="City (e.g. Hyderabad)" className="h-12 pl-12 rounded-full border-[#E2E8F0] text-base" data-testid="doctors-filter-city"/>
          </div>
          <Select value={specialty || 'all'} onValueChange={(v)=>setSpecialty(v==='all'?'':v)}>
            <SelectTrigger className="h-12 rounded-full border-[#E2E8F0]" data-testid="doctors-filter-specialty"><SelectValue placeholder="All specialties"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All specialties</SelectItem>
              {SPECIALTIES.filter(Boolean).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={load} className="h-12 px-6 rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white font-semibold" data-testid="doctors-search-btn">
            <Search className="h-4 w-4 mr-2"/> Search
          </Button>
        </div>
      </Card>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8" data-testid="doctors-list">
        {loading && (
          <div className="col-span-full text-center text-[#7A7A7A] py-10">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2"/>Loading doctors…
          </div>
        )}
        {!loading && doctors.length === 0 && (
          <div className="col-span-full text-center text-[#7A7A7A] py-10">No doctors found. Try a different filter.</div>
        )}
        {doctors.map(d => (
          <Card key={d.id} className="p-6 rounded-3xl border border-[#E8E1D5] bg-white hover:-translate-y-1 transition-all hb-shadow-soft flex flex-col" data-testid={`doctor-card-${d.id}`}>
            <div className="flex items-start gap-4">
              <div className="h-16 w-16 rounded-2xl bg-[#F4F1EA] overflow-hidden shrink-0 grid place-items-center">
                {d.photo_url
                  ? <img src={d.photo_url} alt={d.name} className="h-full w-full object-cover"/>
                  : <Stethoscope className="h-7 w-7 text-[#1E3F33]"/>}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-1.5">
                  <h3 className="font-display text-xl text-[#1A1A1A]">{d.name}</h3>
                  <BadgeCheck className="h-4 w-4 text-[#1E3F33]"/>
                </div>
                <div className="text-sm text-[#4A4A4A] mt-0.5">{d.qualification} · {d.experience_years} yrs</div>
                <div className="flex items-center gap-1 mt-1.5 text-sm">
                  <Star className="h-3.5 w-3.5 fill-[#E07A5F] text-[#E07A5F]"/>
                  <span className="font-semibold">{d.rating}</span>
                  <span className="text-[#7A7A7A]">· {d.specialty}</span>
                </div>
              </div>
            </div>
            <div className="mt-4 space-y-2 text-sm text-[#4A4A4A]">
              <div className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 shrink-0"/><span>{d.clinic}, {d.city}</span></div>
              <div className="flex items-center gap-2"><Languages className="h-4 w-4 shrink-0"/><span>{d.languages.join(', ').toUpperCase()}</span></div>
              <div className="flex items-center gap-2"><IndianRupee className="h-4 w-4 shrink-0"/>
                <span>{d.consultation_fee === 0 ? <Badge className="bg-[#E8F5E9] text-[#2A5244] border-[#2A5244]/20 rounded-full">FREE</Badge> : `₹${d.consultation_fee}`}</span>
              </div>
            </div>
            <p className="text-[14px] text-[#4A4A4A] mt-4 line-clamp-3">{d.bio}</p>
            <div className="mt-4 pt-4 border-t border-[#E8E1D5] flex gap-2">
              <Button onClick={()=>setPicked(d)} disabled={!reportId}
                className="flex-1 rounded-full bg-[#E07A5F] hover:bg-[#C9674D] text-white font-semibold"
                data-testid={`doctor-send-btn-${d.id}`}>
                <Send className="h-4 w-4 mr-2"/> Send report
              </Button>
            </div>
            {!reportId && <div className="text-xs text-[#7A7A7A] mt-2">Generate a report first to send it.</div>}
          </Card>
        ))}
      </div>
      <Dialog open={!!picked} onOpenChange={(v)=>!v && setPicked(null)}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Send to {picked?.name}</DialogTitle>
            <DialogDescription>Add a short note about how you're feeling. Your report is attached.</DialogDescription>
          </DialogHeader>
          {reportPreview?.doctor_handoff && (
            <div className="rounded-2xl border border-[#E8E1D5] bg-[#FFFDF8] p-4 text-sm">
              <div className="font-semibold text-[#1E3F33] mb-2">Doctor preview</div>
              <div className="space-y-1 text-[#4A4A4A]">
                <div><strong className="text-[#1A1A1A]">Main concern:</strong> {reportPreview.doctor_handoff.chief_complaint || 'Not reported'}</div>
                <div><strong className="text-[#1A1A1A]">Duration:</strong> {reportPreview.doctor_handoff.onset_duration || 'Not reported'}</div>
                <div><strong className="text-[#1A1A1A]">Severity:</strong> {reportPreview.doctor_handoff.severity || 'Not reported'}</div>
              </div>
              <div className="mt-3 text-xs text-[#7A7A7A]">Review this before sending. The full report and PDF stay attached.</div>
            </div>
          )}
          <Textarea value={note} onChange={(e)=>setNote(e.target.value)} placeholder="e.g. Doctor, my fever is not reducing since 2 days, please advise."
            className="min-h-28 rounded-2xl border-[#E2E8F0]" data-testid="referral-note-input"/>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setPicked(null)} className="rounded-full" data-testid="referral-cancel-btn">Cancel</Button>
            <Button onClick={sendReferral} disabled={sending}
              className="rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white font-semibold"
              data-testid="referral-send-btn">
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <Send className="h-4 w-4 mr-2"/>}
              Send report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
